import { useEffect } from "react";

import { useUserIdsWithCapability } from "hooks/capabilities";
import { useAppDispatch, useAppSelector } from "hooks/redux_hooks";
import { USER_PICKER_PROPERTIES, useUsersProperties } from "hooks/use_user_properties";
import {
  clearAllBreakoutGroupTitles,
  setAllBreakoutGroupTitles,
  setParticipantBarUserIds,
} from "shared/reducers/classroom";
import { CapabilityType } from "shared/types";
import { FilterStrategyType, pickUsersForParticipantBar } from "shared/utils/user_picker";
import {
  selectActiveBreakoutSessionId,
  selectActiveOrPrecedingSegmentBreakout,
  selectCurrentFilter,
  selectIsInPersonClass,
  selectMaxSubscribedStreams,
  selectParticipantBarUserIds,
  selectStage,
} from "store/selectors/classroom";
import { selectCurrentUserId } from "store/selectors/local";
import { isLeader } from "utils/user";

function ParticipantBarObserver() {
  const dispatch = useAppDispatch();

  const maxSubscribedStreams = useAppSelector(selectMaxSubscribedStreams());

  const currentUserId = useAppSelector(selectCurrentUserId());
  const users = Object.values(useUsersProperties(USER_PICKER_PROPERTIES));
  const canControlClassroomUserIds = useUserIdsWithCapability(CapabilityType.CAN_CONTROL_CLASSROOM);
  const canControlClassroom = canControlClassroomUserIds.includes(currentUserId);
  const isAlwaysSubscribedToUserIds = useUserIdsWithCapability(
    CapabilityType.IS_ALWAYS_SUBSCRIBED_TO,
  );
  const canParticipateInClassUserIds = useUserIdsWithCapability(
    CapabilityType.CAN_PARTICIPATE_IN_CLASS,
  );
  const isVisibleInClassUserIds = useUserIdsWithCapability(CapabilityType.IS_VISIBLE_IN_CLASS);
  const isCurrentUserTheLeader = isLeader(currentUserId, users, isAlwaysSubscribedToUserIds);
  const { panes } = useAppSelector(selectStage());
  const presentVisibleUsers = users.filter(
    (user) => user.is_present && isVisibleInClassUserIds.includes(user.id),
  );
  const presentVisibleUserIds = presentVisibleUsers.map((user) => user.id).sort();
  const presentVisibleUserIdsAsString = JSON.stringify(presentVisibleUserIds);
  const participantBarUserIds = useAppSelector(selectParticipantBarUserIds());
  const currentFilter = useAppSelector(selectCurrentFilter());

  const isInPerson = useAppSelector(selectIsInPersonClass());

  useEffect(() => {
    if (!isCurrentUserTheLeader) return;

    if (isInPerson) {
      dispatch(
        setParticipantBarUserIds({
          userIds: presentVisibleUserIds.filter((userId) =>
            canControlClassroomUserIds.includes(userId),
          ),
          leaderId: currentUserId, // needed for logging purposes
        }),
      );
    } else if (presentVisibleUserIds.length <= maxSubscribedStreams) {
      dispatch(
        setParticipantBarUserIds({
          userIds: presentVisibleUserIds,
          leaderId: currentUserId, // needed for logging purposes
        }),
      );
    } else {
      const sortedUsers = pickUsersForParticipantBar(
        presentVisibleUsers,
        isAlwaysSubscribedToUserIds,
        canParticipateInClassUserIds,
        participantBarUserIds,
        panes,
      );
      const selectedUserIds = sortedUsers.slice(0, maxSubscribedStreams).map((user) => user.id);
      dispatch(
        setParticipantBarUserIds({
          userIds: selectedUserIds,
          leaderId: currentUserId,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    isInPerson,
    canControlClassroomUserIds,
    canParticipateInClassUserIds,
    currentUserId,
    isAlwaysSubscribedToUserIds,
    isCurrentUserTheLeader,
    maxSubscribedStreams,
    presentVisibleUserIdsAsString,
  ]);

  // Set breakout group indicators
  const activeBreakoutSessionId = useAppSelector(selectActiveBreakoutSessionId());
  const breakoutFromActiveOrPrecedingSegment = useAppSelector(
    selectActiveOrPrecedingSegmentBreakout(),
  );
  const isActiveFilter = currentFilter && currentFilter !== FilterStrategyType.LowestFilterCount;
  const isBreakoutFilter = currentFilter && currentFilter.startsWith("breakout_group");
  const filterBreakoutId = isBreakoutFilter && currentFilter.split(":")[1];
  const breakoutId = filterBreakoutId || breakoutFromActiveOrPrecedingSegment?.id;
  const showSpecificBreakoutSignal = !activeBreakoutSessionId && isBreakoutFilter;
  const showRecentBreakoutSignal =
    !activeBreakoutSessionId && !isActiveFilter && !!breakoutFromActiveOrPrecedingSegment;
  const shouldSetBreakoutGroupIndicators = showSpecificBreakoutSignal || showRecentBreakoutSignal;

  useEffect(() => {
    if (!isCurrentUserTheLeader || isInPerson || !canControlClassroom) return;

    if (shouldSetBreakoutGroupIndicators) {
      window.flightLogger.log("[ParticipantBarObserver] Setting breakout group indicators");
      dispatch(setAllBreakoutGroupTitles({ breakoutId }));
    } else {
      window.flightLogger.log("[ParticipantBarObserver] Clearing breakout group indicators");
      dispatch(clearAllBreakoutGroupTitles());
    }
  }, [
    dispatch,
    isCurrentUserTheLeader,
    isInPerson,
    shouldSetBreakoutGroupIndicators,
    breakoutId,
    canControlClassroom,
  ]);

  return null;
}

export { ParticipantBarObserver };
